// src/hooks/useAvatar.js
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'

export function useAvatar(session) {
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [uploading, setUploading] = useState(false)

  const user = session?.user

  const loadAvatar = useCallback(async () => {
    if (!user) return

    // Try Supabase storage for uploaded avatar first
    try {
      const path = `avatars/${user.id}/avatar`
      const { data, error } = await supabase.storage
        .from('magic-avatars')
        .createSignedUrl(path, 3600)
      if (data?.signedUrl && !error) {
        setAvatarUrl(data.signedUrl)
        return
      }
    } catch {}

    // Fall back to Google/OAuth profile picture
    const googleUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture
    if (googleUrl) {
      setAvatarUrl(googleUrl)
      return
    }

    setAvatarUrl(null)
  }, [user])

  useEffect(() => { loadAvatar() }, [loadAvatar])

  const uploadAvatar = useCallback(async (file) => {
    if (!user || !file) return
    setUploading(true)
    try {
      const path = `avatars/${user.id}/avatar`
      const { error } = await supabase.storage
        .from('magic-avatars')
        .upload(path, file, { upsert: true, contentType: file.type })
      if (!error) await loadAvatar()
    } catch (err) {
      console.error('Avatar upload error:', err)
    }
    setUploading(false)
  }, [user, loadAvatar])

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'M'
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return { avatarUrl, uploading, uploadAvatar, initials, displayName }
}
